/* eslint-disable no-restricted-globals */
import React, { useState } from "react";
import { BiChevronRight } from "react-icons/bi";
import { FaUser, FaMoneyBillWave, FaCalendarAlt, FaClock, FaHandshake, FaEdit } from "react-icons/fa";
import {
  ButtonComp,
  CardComp,
  ModalCompAdvanceUpdate,
  MoneyInputComp,
  SelectComp2,
  SelectCompWithFormattedLabel,
  TextInputComp,
  LoanTypeDetails,
  MemberInfoCard,
} from "../../../../components";
import FallbackImage from "../../../../components/Common/ImageWrapper";
import FilterFind from "../../../../components/FilterFind";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  useGetAllMembersQuery,
} from "../../../../store/selectableSlice";
import {
  useViewMemberQuery,
} from "../../../../store/admin/memberManagement/memberSlice";
import { useEnums } from "../../../../hooks/useEnums";
import {
  useCreateLoanRequestMutation,
  useGetSelectableLoanTypesQuery,
  useGetBorrowingEligibilityQuery,
} from "../../../../store/admin/loan";
import NonmemberGuarantorForm from "./NonmemberGuarantorForm";
import toast from "react-hot-toast";

export default function NewApplicationForm({ onNext }) {
  const [selectedMember, setSelectedMember] = useState("");
  const [loanTypeDetails, setLoanTYpeDetails] = useState({});
  const [guarantors, setGuarantors] = useState([]);
  const [nonMembersGuarantorDetails, setNonMemberGuarantorDetails] = useState(
    []
  );
  const [guarantorSlotMapping, setGuarantorSlotMapping] = useState({}); // Track which slot each non-member belongs to
  const [modalPage, setModalPage] = React.useState("");
  const { data: membersData = [] } = useGetAllMembersQuery();
  const enums = useEnums();

  // Fetch detailed member information when a member is selected
  const { data: memberDetails, isLoading: isLoadingMemberDetails } =
    useViewMemberQuery(selectedMember?.value, {
      skip: !selectedMember?.value, // Only fetch when a member is selected
    });

  const { data: loanTypes, isLoading: isLoadingLoanTypes } =
    useGetSelectableLoanTypesQuery();

  const { data: borrowingEligibility } = useGetBorrowingEligibilityQuery(
    {
      memberId: selectedMember?.value,
      loanTypeId: loanTypeDetails?.id || loanTypeDetails?.value,
    },
    {
      skip: !selectedMember?.value || !(loanTypeDetails?.id || loanTypeDetails?.value),
    }
  );

  const formattedLoanTypes = loanTypes?.data?.filter((item) => item && item.id).map((item) => {
    return {
      label: item.name,
      value: item.id,
      max_guarantor: item.max_guarantor,
      min_guarantor: item.min_guarantor,
      min_loan_amount: item.min_loan_amount,
      max_loan_amount: item.max_loan_amount,
      rate: item.rate,
      interest_calculation_mode: item.interest_calculation_mode,
      loan_fee: item.loan_fee,
      // Include all other loan type fields for the details component
      ...item,
    };
  });

  React.useEffect(() => {
    const maxG = Number(loanTypeDetails?.max_guarantor ?? 0);
    if (maxG > 0) {
      const initialGuarantors = Array.from({ length: maxG }, (_, index) => ({
        label: `Guarantor ${index + 1}`,
        value: "",
      }));
      setGuarantors(initialGuarantors);
    } else {
      setGuarantors([]);
      setNonMemberGuarantorDetails([]);
      setGuarantorSlotMapping({});
    }
  }, [loanTypeDetails?.max_guarantor]);

  const { allMembers } = React.useMemo(() => {
    const fetchedMembers = Array.isArray(membersData) ? membersData : [];
    const formattedMembers = fetchedMembers
      .filter((item) => item && item.id)
      .map((item) => {
        const first = item.first_name || "";
        const last = item.last_name || "";
        const name =
          `${first} ${last}`.trim() || item.name || "Unknown";
        const canGuarantee = item.can_guarantee !== false;
        const reason = item.guarantee_block_reason || null;
        return {
          label: reason ? `${name} (${reason})` : name,
          name,
          photo: item.photo,
          userId: item.member_no,
          value: item.id,
          isDisabled: !canGuarantee,
          can_guarantee: canGuarantee,
          guarantee_block_reason: reason,
        };
      });
    return { allMembers: formattedMembers };
  }, [membersData]);

  const handleGuarantorChange = (index, value, setFieldValue) => {
    if (value && !String(value).includes("non_member_guarantor")) {
      const option = allMembers.find((m) => m.value === value);
      if (option?.isDisabled) {
        toast.error(option.guarantee_block_reason || "This member cannot guarantee");
        return;
      }
    }
    const updatedGuarantors = [...guarantors];
    updatedGuarantors[index].value = value;
    setGuarantors(updatedGuarantors);
    setFieldValue(`guarantor${index + 1}`, value);
  };

  const isInformalLoan = (loanTypeDetails?.loan_model ?? "").toLowerCase() === "informal";
  const effectiveMaxAmount = Number(
    borrowingEligibility?.eligible_max ?? loanTypeDetails?.max_loan_amount ?? Infinity
  );
  const typeMinAmount = Number(
    borrowingEligibility?.type_min ?? loanTypeDetails?.min_loan_amount ?? 0
  );

  const initialValues = {
    loan_type_id: "",
    duration: "",
    duration_type: "",
    amount: "",
    ...guarantors?.reduce((acc, guarantor, index) => {
      return { ...acc, [`guarantor${index + 1}`]: guarantor.value };
    }, {}),
  };

  const validationSchema = React.useMemo(
    () =>
      Yup.object({
        loan_type_id: Yup.string().required("Required"),
        duration: isInformalLoan ? Yup.number() : Yup.number().required("Required"),
        duration_type: isInformalLoan ? Yup.string() : Yup.string().required("Required"),
        amount: Yup.number()
          .required("Required")
          .min(
            typeMinAmount || 0,
            `Amount must be at least ₦${(typeMinAmount || 0).toLocaleString()}`
          )
          .max(
            Number.isFinite(effectiveMaxAmount) ? effectiveMaxAmount : Infinity,
            `Amount cannot exceed ₦${(
              Number.isFinite(effectiveMaxAmount) ? effectiveMaxAmount : 0
            ).toLocaleString()}`
          ),
      }),
    [isInformalLoan, typeMinAmount, effectiveMaxAmount]
  );
  const getMemberGuarantors = (values) => {
    return Object.keys(values)
      .filter((key) => key.startsWith("guarantor") && values[key])
      .map((key) => values[key]);
  };

  const filterNonMemberGuarantors = (guarantors) => {
    return guarantors.filter(
      (item) =>
        typeof item !== "string" || !item.includes("non_member_guarantor")
    );
  };

  const [createLoanRequest, { isLoading: isCreating }] =
    useCreateLoanRequestMutation();

  const handleFormSubmit = async (values, { resetForm }) => {
    if (!selectedMember?.value) {
      return toast.error("Please select a member");
    }
    if (borrowingEligibility && !borrowingEligibility.can_apply) {
      return toast.error(
        borrowingEligibility.message ||
          "This member cannot apply for this loan type with current savings and shares."
      );
    }
    const member_id = selectedMember.value;
    let { loan_type_id, duration, duration_type, amount } = values;

    // Informal: duration/plan not used; server derives plan from duration_type for formal
    if (isInformalLoan) {
      duration = 0;
      duration_type = "MONTHS";
    }

    // Get all guarantors from form values
    const allGuarantors = getMemberGuarantors(values);
    
    // Filter out non-member guarantor placeholders
    let formattedMemberGuarantors = filterNonMemberGuarantors(allGuarantors);
    
    // Convert to numbers and filter out invalid values
    formattedMemberGuarantors = formattedMemberGuarantors
      .map((id) => {
        // Convert to number if it's a string
        const numId = typeof id === "string" ? parseInt(id, 10) : id;
        // Return only valid numbers
        return !isNaN(numId) && numId > 0 ? numId : null;
      })
      .filter((id) => id !== null); // Remove null values
    
    // Remove duplicates
    formattedMemberGuarantors = [...new Set(formattedMemberGuarantors)];
    
    // Remove the applicant member from guarantors (member can't be their own guarantor)
    formattedMemberGuarantors = formattedMemberGuarantors.filter(
      (guarantorId) => guarantorId !== member_id
    );

    // Count non-member guarantors
    const nonMemberGuarantorsCount = nonMembersGuarantorDetails?.length || 0;
    
    // Total guarantors = member guarantors + non-member guarantors
    const totalGuarantors = formattedMemberGuarantors.length + nonMemberGuarantorsCount;

    // Validate minimum guarantors if required
    const minGuarantors = loanTypeDetails?.min_guarantor || 0;
    if (minGuarantors > 0 && totalGuarantors < minGuarantors) {
      return toast.error(
        `Please select at least ${minGuarantors} guarantor(s)`
      );
    }

    // Validate that we have guarantors if max_guarantor is set
    if (loanTypeDetails?.max_guarantor && loanTypeDetails?.max_guarantor > 0) {
      if (totalGuarantors === 0 && minGuarantors === 0) {
        // If no guarantors selected but max is set, check if it's optional
        // For now, we'll allow empty if min is 0
      } else if (totalGuarantors > loanTypeDetails.max_guarantor) {
        return toast.error(
          `Maximum ${loanTypeDetails.max_guarantor} guarantor(s) allowed`
        );
      }
    }

    const payload = {
      member_id,
      loan_type_id,
      duration,
      duration_type,
      amount,
      member_guarantors: formattedMemberGuarantors,
      non_member_guarantors: (nonMembersGuarantorDetails || []).map((g) => ({
        ...g,
        phone: String(g?.phone ?? "").trim(),
        email: String(g?.email ?? "").trim(),
        name: String(g?.name ?? "").trim(),
      })),
    };

    console.log("Loan Application Payload:", payload); // Debug log

    const response = await createLoanRequest(payload);
    const { data, error } = response;
    if (error) {
      return toast.error(error?.data?.message || "An error occured");
    } else if (data?.status === false) {
      return toast.error(data?.message || "An error occured");
    } else {
      toast.success(data?.message || "Success");
      setNonMemberGuarantorDetails([]);
      setGuarantorSlotMapping({});
      setEditingGuarantorIndex(null);
      setLoanTYpeDetails({});
      setGuarantors([]);
      setSelectedMember("");
      resetForm();
    }
  };

  const [currentGuarantorSlot, setCurrentGuarantorSlot] = useState(null);
  const [editingGuarantorIndex, setEditingGuarantorIndex] = useState(null);

  const useModal = [
    {
      name: "NonMemberGuarantor",
      component: (
        <NonmemberGuarantorForm
          Back={() => {
            setModalPage("");
            setCurrentGuarantorSlot(null);
            setEditingGuarantorIndex(null);
          }}
          setNonMemberGuarantorDetails={(callback) => {
            // The form passes a function: (initial) => [...initial, values]
            setNonMemberGuarantorDetails((prev) => {
              const newDetails = typeof callback === 'function' ? callback(prev) : [...prev, callback];
              // Track which slot this guarantor belongs to (only for new guarantors)
              if (currentGuarantorSlot !== null && editingGuarantorIndex === null) {
                setGuarantorSlotMapping((prevMapping) => ({
                  ...prevMapping,
                  [currentGuarantorSlot]: newDetails.length - 1, // Index in nonMembersGuarantorDetails array
                }));
              }
              return newDetails;
            });
            setModalPage("");
            setCurrentGuarantorSlot(null);
            setEditingGuarantorIndex(null);
          }}
          initialData={
            editingGuarantorIndex !== null
              ? nonMembersGuarantorDetails[editingGuarantorIndex]
              : null
          }
          editIndex={editingGuarantorIndex}
        />
      ),
    },
  ];
  return (
    <section className="px-lg-3 min-vh-100 pb-5 mb-3">
      <ModalCompAdvanceUpdate
        pageName={modalPage}
        arrayComp={useModal}
        handleClose={() => setModalPage()}
        centered
        size={"lg"}
      />
      <FilterFind rightSide={false} title="New Loan Application" />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ values, errors, touched, setFieldValue }) => (
          <Form>
            {/* Member Selection Section */}
            <CardComp 
              cardBodyClassName={"row py-0 gx-5 gy-4"}
              cardClassName={"border-0 rounded-10 px-lg-4 mb-4 pt-4 shadow3"}
            >
              <div className="col-12 mb-2">
                <h5 className="fw-bold mb-1 text-black1">
                  Member Information
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                  Select the member applying for the loan
                </p>
              </div>
              
              <div className="col-12">
                <label className="d-flex align-items-center mb-2 fw-bold text-black1">
                  <FaUser className="me-2" style={{ color: "var(--color-primary)", fontSize: "16px" }} />
                  Member Name
                  <span className="text-danger ms-1">*</span>
                </label>
                <div className="d-flex gap-2 align-items-end">
                  <div className="flex-grow-1">
                    <SelectCompWithFormattedLabel
                      setOption={setSelectedMember}
                      arrayComp={allMembers}
                      selectedOption={selectedMember}
                      placeholder="Search and select a member"
                    />
                  </div>
                  {selectedMember && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm mb-1"
                      onClick={() => setSelectedMember("")}
                      title="Clear member selection"
                      style={{ minWidth: "40px", height: "38px" }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>

              {selectedMember && (
                <div className="col-12 mt-2">
                  {isLoadingMemberDetails ? (
                    <div className="d-flex justify-content-center p-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <MemberInfoCard
                      member={{
                        ...selectedMember,
                        ...memberDetails,
                        // credit_score: memberDetails?.credit_score || 0, // paused until credit score is implemented
                        total_loans: memberDetails?.total_loans || 0,
                        active_loans: memberDetails?.active_loans || 0,
                        repayment_rate: memberDetails?.repayment_rate || 0,
                        is_eligible: memberDetails?.is_eligible ?? true,
                        branch_name:
                          memberDetails?.branch?.name || "Main Branch",
                        created_at:
                          memberDetails?.created_at || new Date().toISOString(),
                      }}
                    />
                  )}
                </div>
              )}
            </CardComp>

            {/* Loan Details Section */}
            <CardComp 
              cardBodyClassName={"row py-0 gx-5 gy-4"}
              cardClassName={"border-0 rounded-10 px-lg-4 mb-4 pt-4 shadow3"}
            >
              <div className="col-12 mb-2">
                <h5 className="fw-bold mb-1 text-black1">
                  Loan Details
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                  Enter the loan information and terms
                </p>
              </div>

              {/* Loan Type - Full Width */}
              <div className="col-12">
                <label className="d-flex align-items-center mb-2 fw-bold text-black1">
                  <FaHandshake className="me-2" style={{ color: "var(--color-primary)", fontSize: "16px" }} />
                  Loan Type
                  <span className="text-danger ms-1">*</span>
                </label>
                <SelectComp2
                  placeholder={"Select loan type"}
                  arrayComp={formattedLoanTypes}
                  isLoading={isLoadingLoanTypes}
                  selectedOption={values.loan_type_id}
                  setSelectedOption={(selectedOption) => {
                    if (selectedOption) {
                      setLoanTYpeDetails(selectedOption);
                      setFieldValue("loan_type_id", selectedOption.value);
                    } else {
                      setLoanTYpeDetails({});
                      setFieldValue("loan_type_id", "");
                    }
                  }}
                  errors={
                    touched.loan_type_id && errors.loan_type_id
                      ? [errors.loan_type_id]
                      : []
                  }
                />
              </div>

              {/* Loan Type Details */}
              {loanTypeDetails?.id && (
                <div className="col-12 mt-2">
                  <LoanTypeDetails loanType={loanTypeDetails} durationType={values.duration_type} />
                </div>
              )}

              {/* Loan Amount - Full Width */}
              <div className="col-12">
                <label className="d-flex align-items-center mb-2 fw-bold text-black1">
                  <FaMoneyBillWave className="me-2" style={{ color: "var(--color-primary)", fontSize: "16px" }} />
                  Loan Amount
                  <span className="text-danger ms-1">*</span>
                </label>
                <MoneyInputComp
                  placeholder={"Enter loan amount"}
                  descClassName="text-muted"
                  desc={
                    borrowingEligibility
                      ? `Eligible max ₦${Number(
                          borrowingEligibility.eligible_max || 0
                        ).toLocaleString()} (savings + shares × ${
                          borrowingEligibility.multiplier
                        }${
                          Number.isFinite(typeMinAmount)
                            ? `, min ₦${typeMinAmount.toLocaleString()}`
                            : ""
                        })`
                      : loanTypeDetails?.min_loan_amount &&
                        loanTypeDetails?.max_loan_amount
                      ? `Amount must be between ₦${loanTypeDetails.min_loan_amount.toLocaleString()} - ₦${loanTypeDetails.max_loan_amount.toLocaleString()}`
                      : "Enter the loan amount"
                  }
                  name={"amount"}
                />
                {borrowingEligibility && !borrowingEligibility.can_apply && (
                  <small className="text-danger d-block mt-1">
                    {borrowingEligibility.message ||
                      "This member cannot apply for this loan type with current savings and shares."}
                  </small>
                )}
              </div>

              {/* Duration, Duration Type, Repayment Plan - not used for informal (pay at will) */}
              {!isInformalLoan && (
                <>
                  <div className="col-12 mb-3 mt-2">
                    <h6 className="fw-bold mb-1 text-black1">
                      Loan Duration
                    </h6>
                    <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                      Set the loan repayment period
                    </p>
                  </div>

                  <div className="col-12 col-lg-6">
                    <label className="d-flex align-items-center mb-2 fw-bold text-black1">
                      <FaClock className="me-2" style={{ color: "var(--color-primary)", fontSize: "16px" }} />
                      Duration
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <TextInputComp
                      placeholder={"Enter duration (e.g., 12)"}
                      name="duration"
                      type="number"
                    />
                  </div>

                  <div className="col-12 col-lg-6">
                    <label className="d-flex align-items-center mb-2 fw-bold text-black1">
                      <FaCalendarAlt className="me-2" style={{ color: "var(--color-primary)", fontSize: "16px" }} />
                      Duration Type
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <SelectComp2
                      placeholder={"Select duration type"}
                      arrayComp={enums["DurationType"] ?? []}
                      selectedOption={values.duration_type}
                      setSelectedOption={(selectedOption) =>
                        setFieldValue("duration_type", selectedOption?.value || "")
                      }
                      errors={
                        touched.duration_type && errors.duration_type
                          ? [errors.duration_type]
                          : []
                      }
                    />
                  </div>

                  <div className="col-12">
                    <p className="text-muted mb-0 small">
                      Repayments follow the duration type (e.g. months → monthly).
                    </p>
                  </div>
                </>
              )}
              {isInformalLoan && (
                <div className="col-12 mt-2">
                  <p className="text-muted mb-0 small">
                    Informal loans have no fixed term or schedule; the member can pay any amount at any time.
                  </p>
                </div>
              )}
            </CardComp>

            {/* Guarantors Section — hidden when max is 0 */}
            {Number(loanTypeDetails?.max_guarantor) > 0 && guarantors.length > 0 && (
              <CardComp 
                cardBodyClassName={"row py-0 gx-5 gy-4"}
                cardClassName={"border-0 rounded-10 px-lg-4 mb-4 pt-4 shadow3"}
              >
                <div className="col-12 mb-2">
                  <h5 className="fw-bold mb-1 text-black1 d-flex align-items-center">
                    <FaHandshake className="me-2" style={{ color: "var(--color-primary)", fontSize: "18px" }} />
                    Guarantors
                  </h5>
                  <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                    Select {loanTypeDetails?.min_guarantor || 0} - {loanTypeDetails?.max_guarantor || 0} guarantor(s) for this loan
                    {loanTypeDetails?.min_guarantor > 0 && (
                      <span className="text-danger ms-1">
                        (Minimum {loanTypeDetails.min_guarantor} required)
                      </span>
                    )}
                    . Members on an active disbursed loan appear greyed out when ineligible.
                  </p>
                </div>
                {guarantors?.map((guarantor, index) => {
                  // Get currently selected guarantors (excluding current field)
                  const selectedGuarantorIds = guarantors
                    .map((g, i) => (i !== index && g.value ? g.value : null))
                    .filter((id) => id && typeof id !== "string" || !id?.includes("non_member_guarantor"));
                  
                  // Get selected member ID if exists
                  const selectedMemberId = selectedMember?.value || null;
                  
                  // Filter out already selected guarantors and the applicant member
                  // Keep ineligible members visible but disabled
                  const availableGuarantors = allMembers.filter((member) => {
                    const memberId = member.value;
                    // Don't show if already selected as another guarantor
                    if (selectedGuarantorIds.includes(memberId)) {
                      return false;
                    }
                    // Don't show if it's the applicant member
                    if (selectedMemberId && memberId === selectedMemberId) {
                      return false;
                    }
                    return true;
                  });

                  // Check if this slot has a non-member guarantor
                  const nonMemberIndex = guarantorSlotMapping[index];
                  const nonMemberGuarantor = nonMemberIndex !== undefined 
                    ? nonMembersGuarantorDetails[nonMemberIndex] 
                    : null;

                  return (
                    <div className="col-12 col-lg-6" key={index}>
                      <label className="d-flex align-items-center mb-2 fw-bold text-black1">
                        <FaUser className="me-2" style={{ color: "var(--color-primary)", fontSize: "16px" }} />
                        {guarantor.label}
                        {index < (loanTypeDetails?.min_guarantor || 0) && (
                          <span className="text-danger ms-1">*</span>
                        )}
                      </label>
                      <SelectComp2
                        placeholder={`Select guarantor ${index + 1}`}
                        isOptionDisabled={(option) => Boolean(option?.isDisabled)}
                        arrayComp={[
                          ...availableGuarantors,
                          {
                            value: `non_member_guarantor_${index + 1}`,
                            label: (
                              <ButtonComp
                                btnClassName={
                                  "w-100 bg-primary1 p-2 text-white border-0 rounded"
                                }
                                btnText={
                                  <div
                                    className="d-flex justify-content-between align-items-center"
                                    onClick={() => {
                                      scroll(0, 0);
                                      setCurrentGuarantorSlot(index);
                                      setModalPage("NonMemberGuarantor");
                                    }}
                                  >
                                    Non - member
                                    <BiChevronRight size={20} />
                                  </div>
                                }
                              />
                            ),
                          },
                        ]}
                        selectedOption={
                          nonMemberGuarantor 
                            ? `non_member_guarantor_${index + 1}` 
                            : values[`guarantor${index + 1}`]
                        }
                        setSelectedOption={(selectedOption) =>
                          handleGuarantorChange(
                            index,
                            selectedOption?.value || "",
                            setFieldValue
                          )
                        }
                        errors={
                          touched[`guarantor${index + 1}`] &&
                          errors[`guarantor${index + 1}`]
                            ? [errors[`guarantor${index + 1}`]]
                            : []
                        }
                      />
                      {/* Display non-member guarantor details if added */}
                      {nonMemberGuarantor && (
                        <div 
                          className="mt-2 p-3 rounded"
                          style={{
                            backgroundColor: "#f7f7f7",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="fw-bold text-black1 mb-0" style={{ fontSize: "14px" }}>
                              External Guarantor Details
                            </h6>
                            <div className="d-flex gap-2 align-items-center">
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => {
                                  // Edit this non-member guarantor
                                  setEditingGuarantorIndex(nonMemberIndex);
                                  setCurrentGuarantorSlot(index);
                                  setModalPage("NonMemberGuarantor");
                                }}
                                style={{ 
                                  color: "var(--color-secondary)", 
                                  border: "1px solid var(--color-secondary)",
                                  backgroundColor: "transparent",
                                  padding: "4px 8px",
                                  borderRadius: "5px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px"
                                }}
                                title="Edit this guarantor"
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = "var(--color-secondary-soft)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = "transparent";
                                }}
                              >
                                <FaEdit size={14} />
                                <span style={{ fontSize: "12px" }}>Edit</span>
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => {
                                  // Remove this non-member guarantor
                                  const newDetails = nonMembersGuarantorDetails.filter((_, i) => i !== nonMemberIndex);
                                  setNonMemberGuarantorDetails(newDetails);
                                  // Update mapping
                                  const newMapping = { ...guarantorSlotMapping };
                                  delete newMapping[index];
                                  // Reindex remaining mappings
                                  Object.keys(newMapping).forEach((slot) => {
                                    const oldIndex = newMapping[slot];
                                    if (oldIndex > nonMemberIndex) {
                                      newMapping[slot] = oldIndex - 1;
                                    }
                                  });
                                  setGuarantorSlotMapping(newMapping);
                                  // Clear the form field
                                  setFieldValue(`guarantor${index + 1}`, "");
                                }}
                                style={{ 
                                  color: "#ff4343", 
                                  border: "1px solid #ff4343",
                                  backgroundColor: "transparent",
                                  padding: "4px 8px",
                                  borderRadius: "5px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px"
                                }}
                                title="Remove this guarantor"
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = "rgba(255, 67, 67, 0.1)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = "transparent";
                                }}
                              >
                                <i className="fas fa-times" style={{ fontSize: "14px" }}></i>
                                <span style={{ fontSize: "12px" }}>Remove</span>
                              </button>
                            </div>
                          </div>
                          <div className="d-flex flex-column gap-1" style={{ fontSize: "13px" }}>
                            <div>
                              <span className="text-muted">Name:</span>{" "}
                              <span className="fw-bold text-black1">{nonMemberGuarantor.name || "N/A"}</span>
                            </div>
                            <div>
                              <span className="text-muted">Email:</span>{" "}
                              <span className="fw-bold text-black1">{nonMemberGuarantor.email || "N/A"}</span>
                            </div>
                            <div>
                              <span className="text-muted">Phone:</span>{" "}
                              <span className="fw-bold text-black1">{nonMemberGuarantor.phone || "N/A"}</span>
                            </div>
                            <div>
                              <span className="text-muted">Occupation:</span>{" "}
                              <span className="fw-bold text-black1">{nonMemberGuarantor.occupation || "N/A"}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardComp>
            )}

            {/* Submit Button */}
            <div className="d-flex justify-content-end gap-3 mt-4">
              <ButtonComp
                type="submit"
                btnText={"Submit Application"}
                onClick={() => {}}
                btnClassName={
                  "bg-primary1 py-2 px-5 fw-bold text-white border-0 rounded"
                }
                style={{ minWidth: "200px" }}
                loading={isCreating}
              />
            </div>
          </Form>
        )}
      </Formik>
    </section>
  );
}
